import { useCallback, useEffect, useState } from 'react';

import moment from 'moment';
import 'moment/locale/pt-br';

import * as FinanceiroAPI from '../../../../../../api/financeiro';
import { formatarNumero } from '../../../../../../utils/masks';

export function useLimiteCentroCustoModule({
    dados,
    setDados,
    novoProduto,
    valorParcelaMovimento = 0,
    totalValorMovimento = 0,
    operacaoParametros,
    profile,
    centroCustoLimiteBloquearNaDigitacao,
    centroCustoLimiteBloquear,
}) {
    const [mensagemLimite, setMensagemLimite] = useState(null);
    const [limites, setLimites] = useState({
        limite: 0,
        limiteUtilizado: 0,
        limiteDisponivel: 0,
        carregado: false,
    });

    const buscarLimite = useCallback(
        async ({ primeiraDataVencimento, empresaId, centroCustoId }) => {
            if (!primeiraDataVencimento || !empresaId || !centroCustoId) return null;

            try {
                const response = await FinanceiroAPI.buscaLimiteByCentroCusto(primeiraDataVencimento, empresaId, centroCustoId);

                const limite = Number(response?.limite ?? 0);
                const limiteUtilizado = Number(response?.limiteUtilizado ?? 0);
                const limiteDisponivel = Number(response?.disponivel ?? 0);

                setLimites({
                    limite,
                    limiteUtilizado,
                    limiteDisponivel,
                    carregado: true,
                });

                setDados((prev) => ({
                    ...prev,
                    centroCustoDescricao: response?.centroCusto,
                    limite,
                    limiteUtilizado,
                    limiteDisponivel,
                }));

                if (limiteDisponivel < 0) {
                    setMensagemLimite({
                        key: 'limite-excedido',
                        texto: `Limite de crédito excedido, Limite: R$ ${formatarNumero(Number(limite).toFixed(2))}, limite utilizado R$ ${formatarNumero(Number(limiteUtilizado).toFixed(2))}, valor disponível: ${formatarNumero(Number(limiteDisponivel).toFixed(2))}`,
                        tipo: 'error',
                        tempo: 6,
                    });
                }

                return {
                    limite,
                    limiteUtilizado,
                    limiteDisponivel,
                    centroCusto: response?.centroCusto,
                };
            } catch (error) {
                console.error('Erro ao buscar limite por centro de custo:', error);
                return null;
            }
        },
        [setDados]
    );

    const limiteAtual = Number(limites?.limite || 0);
    const limiteDisponivelBase = Number(limites?.limiteDisponivel || 0);
    const valorParcelaMovimentoAtual = Number(valorParcelaMovimento || 0);

    let isEdicao = Boolean(dados?.id);

    if (operacaoParametros?.geraFinanceiro === false) {
        isEdicao = false;
    }

    const limiteDisponivelAjustado = limites.carregado
        ? Number((limiteDisponivelBase - (isEdicao ? 0 : valorParcelaMovimentoAtual)).toFixed(2))
        : 0;

    const valorTotalAtual = Number(novoProduto?.valorTotal || 0);
    const parcelaAtual = Number(dados?.Prazo?.parcela || 1);
    const valorParcelaAtual = Number(valorTotalAtual / parcelaAtual) || 0;
    const quantidadeInformada = Number(novoProduto?.quantidade || 0) > 0;

    const semLimiteCadastrado = !!centroCustoLimiteBloquear && limites.carregado && limiteAtual <= 0;

    const bloqueadoPorLimiteGeral =
        !!centroCustoLimiteBloquear && limites.carregado && (limiteDisponivelAjustado < 0 || semLimiteCadastrado);

    const bloqueadoPorLimiteDigitacao =
        !!centroCustoLimiteBloquear &&
        !!centroCustoLimiteBloquearNaDigitacao &&
        limites.carregado &&
        quantidadeInformada &&
        valorParcelaAtual > limiteDisponivelAjustado &&
        profile?.parametros?.itens?.quantidadePadrao === 0;

    const bloqueadoPorLimiteItem = bloqueadoPorLimiteGeral || bloqueadoPorLimiteDigitacao;

    // Busca limite base quando muda centro, empresa ou vencimento
    useEffect(() => {
        if (!centroCustoLimiteBloquear) return;

        buscarLimite({
            primeiraDataVencimento: dados?.primeiraDataVencimento,
            empresaId: dados?.empresaId,
            centroCustoId: dados?.centroCustoId,
        });
    }, [centroCustoLimiteBloquear, dados?.primeiraDataVencimento, dados?.empresaId, dados?.centroCustoId, buscarLimite]);

    // Busca limite do mês da primeira parcela durante a digitação
    useEffect(() => {
        if (!centroCustoLimiteBloquear) return;
        if (!centroCustoLimiteBloquearNaDigitacao) return;

        const primeiraParcela = Number(dados?.Prazo?.primeiraParcela || 0);

        const primeiraDataVencimento =
            moment.isMoment(dados?.primeiraDataVencimento) || moment(dados?.primeiraDataVencimento).isValid()
                ? moment(dados?.primeiraDataVencimento).clone().add(primeiraParcela, 'days').toDate()
                : null;

        buscarLimite({
            primeiraDataVencimento,
            empresaId: dados?.empresaId,
            centroCustoId: dados?.centroCustoId,
        });
    }, [
        centroCustoLimiteBloquear,
        centroCustoLimiteBloquearNaDigitacao,
        dados?.primeiraDataVencimento,
        dados?.empresaId,
        dados?.centroCustoId,
        dados?.Prazo?.primeiraParcela,
        buscarLimite,
    ]);

    // Mensagem de limite durante a digitação
    useEffect(() => {
        if (!centroCustoLimiteBloquear) return;
        if (!centroCustoLimiteBloquearNaDigitacao) return;
        if (!limites.carregado) return;

        const limiteDisponivelParaDigitacao = Number(limiteDisponivelAjustado || 0);

        const valorTotal = Number(novoProduto?.valorTotal || 0);
        const parcela = Number(dados?.Prazo?.parcela || 1);
        const valorParcela = Number(valorTotal / parcela) || 0;
        const quantidadeInformada = Number(novoProduto?.quantidade || 0) > 0;

        const deveAvisarLimite =
            quantidadeInformada &&
            valorParcela > limiteDisponivelParaDigitacao &&
            profile?.parametros?.itens?.quantidadePadrao === 0;

        if (deveAvisarLimite) {
            setMensagemLimite({
                key: `limite-indisponivel-${Date.now()}`,
                texto: 'Limite de crédito indisponível',
                tipo: 'warning',
                tempo: 6,
                msgPosition: 'top',
            });
        } else {
            setMensagemLimite(null);
        }
    }, [
        centroCustoLimiteBloquear,
        centroCustoLimiteBloquearNaDigitacao,
        limites.carregado,
        limiteDisponivelAjustado,
        novoProduto?.quantidade,
        novoProduto?.valorTotal,
        dados?.Prazo?.parcela,
        profile?.parametros?.itens?.quantidadePadrao,
    ]);

    return {
        state: {
            limites: { ...limites, limiteDisponivelAjustado },
            mensagemLimite,
            bloqueadoPorLimiteGeral,
            bloqueadoPorLimiteDigitacao,
            bloqueadoPorLimiteItem,
        },
        actions: {
            buscarLimite,
            setLimites,
        },
    };
}
